"use client";

import { useMemo } from "react";
import useSWR from "swr";
import type { Product } from "@/types";
import type {
  ModelSummary,
  StationDetail,
  StationHubDataset,
  StationSummary,
} from "@/types/mobility";
import {
  buildStationHubData,
  buildStationHubDataFromDataset,
} from "@/lib/mobility/station-data";
import { fetchStationDetailsAction } from "@/app/actions/stations";
import { STATION_DETAILS_CHUNK } from "@/lib/mobility/station-hub";
import type { ProductFacets } from "@/lib/mobility/bike-facets";

export type { ModelSummary, StationDetail, StationHubDataset, StationSummary };

export interface StationHubData {
  /** 상품 수 내림차순으로 정렬된 지점 집계 */
  stations: StationSummary[];
  stationsById: Map<string, StationSummary>;
  /** 요금제 접미사를 뗀 차종 단위 묶음 */
  models: ModelSummary[];
  /** 상품 id → 필터 축 값(클래스·제조사·타입·변속·라이딩) */
  facets: Map<string, ProductFacets>;
  /** 택배(국내 배송)로도 받을 수 있는 상품 id */
  shippable: Set<string>;
  /** 서버에서 읽어온 지점 상세 (도착 전에는 빈 Map) */
  details: Map<string, StationDetail>;
  loadingDetails: boolean;
}

const EMPTY_DETAILS: Map<string, StationDetail> = new Map();

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * 모빌리티 지점 허브의 데이터 축 (설계 §2-1).
 *
 * 집계는 순수 함수(`buildStationHubData`)를 useMemo 로 감싼 것뿐이고, 지점 상세는
 * SWR 로 읽는다 — effect 안에서 setState 하는 형태(레포 ESLint 금지 규칙)를 쓰지
 * 않기 위해서다. 상세가 오기 전에도 `stations` 는 좌표·상품 수를 들고 있으므로
 * 핀과 개수는 즉시 그려진다.
 */
export function useStationHubData(
  products: Product[],
  locale: string,
  /**
   * 서버가 미리 조립해 내려준 데이터셋(`lib/api/station-hub`). 있으면 이걸
   * **유일한 소스**로 쓴다 — 지점 상세가 이미 들어 있어 클라이언트 재조회
   * (폭포수·스켈레톤)가 통째로 사라진다. null 이면(조회 실패) 아래 상품 기반
   * 경로로 폴백한다.
   */
  dataset?: StationHubDataset | null,
): StationHubData {
  const fromDataset = useMemo(
    () => (dataset ? buildStationHubDataFromDataset(dataset, locale) : null),
    [dataset, locale],
  );

  const fallback = useMemo(
    // 데이터셋이 있으면 상품 집계는 아예 돌리지 않는다(같은 결과를 두 번 만드는 비용).
    () => (dataset ? null : buildStationHubData(products, locale)),
    [dataset, products, locale],
  );

  // 정렬된 id 목록 = SWR 캐시 키. 같은 지점 집합이면 필터를 바꿔도 재요청하지 않는다.
  // 데이터셋 모드에서는 키가 null 이라 요청 자체가 없다.
  const branchIds = useMemo(
    () => (fallback ? fallback.stations.map((s) => s.id).sort() : []),
    [fallback],
  );
  const idsKey = branchIds.join(",");

  const { data, isLoading } = useSWR(
    idsKey ? (["station-details", locale, idsKey] as const) : null,
    async () => {
      const pages = await Promise.all(
        chunk(branchIds, STATION_DETAILS_CHUNK).map((ids) =>
          fetchStationDetailsAction(ids, locale),
        ),
      );
      return pages.flat();
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 10 * 60 * 1000,
      keepPreviousData: true,
    },
  );

  const fetchedDetails = useMemo(() => {
    if (!data || data.length === 0) return EMPTY_DETAILS;
    return new Map(data.map((detail) => [detail.id, detail]));
  }, [data]);

  if (fromDataset) {
    return {
      stations: fromDataset.stations,
      stationsById: fromDataset.stationsById,
      models: fromDataset.models,
      facets: fromDataset.facets,
      shippable: fromDataset.shippable,
      details: fromDataset.details,
      loadingDetails: false,
    };
  }

  return {
    stations: fallback!.stations,
    stationsById: fallback!.stationsById,
    models: fallback!.models,
    facets: fallback!.facets,
    shippable: fallback!.shippable,
    details: fetchedDetails,
    loadingDetails: isLoading,
  };
}
